import React, { useState, useEffect } from 'react';
import Icon from '@/components/AppIcon';
import Button from '@/components/ui/Button';
import Input from '@/components/ui/Input';
import { Checkbox } from '@/components/ui/Checkbox';
import useTranslation from '@/hooks/useTranslation';

const AnnouncementModal = ({ isOpen, onClose, onSend, locations, departments }) => {
  const { t } = useTranslation(['internalMessaging', 'common']);
  const translate = (key, fallback) => t(`internalMessaging:${key}`, fallback);
  const [title, setTitle] = useState('');
  const [message, setMessage] = useState('');
  const [priority, setPriority] = useState('normal');
  const [selectedLocations, setSelectedLocations] = useState([]);
  const [selectedDepartments, setSelectedDepartments] = useState([]);
  const [requireReadReceipt, setRequireReadReceipt] = useState(false);
  const [scheduleFor, setScheduleFor] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!isOpen) {
      setError(null);
      setIsSubmitting(false);
    }
  }, [isOpen]);

  const handleSubmit = async (e) => {
    e?.preventDefault();

    if (!title?.trim() || !message?.trim() || isSubmitting) return;

    setIsSubmitting(true);
    setError(null);

    try {
      await onSend?.({
        title: title?.trim(),
        message: message?.trim(),
        priority,
        locations: selectedLocations,
        departments: selectedDepartments,
        requireReadReceipt,
        scheduleFor: scheduleFor || null,
        timestamp: new Date(),
      });

      setTitle('');
      setMessage('');
      setPriority('normal');
      setSelectedLocations([]);
      setSelectedDepartments([]);
      setRequireReadReceipt(false);
      setScheduleFor('');
      onClose();
    } catch (err) {
      const responseMessage = err?.response?.data?.message;
      const errorMessage = Array.isArray(responseMessage)
        ? responseMessage.join('. ')
        : responseMessage
          ?? err?.message
          ?? translate('errors.announcement', 'Unable to send announcement.');
      setError(errorMessage);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleLocationChange = (locationId, checked) => {
    if (checked) {
      setSelectedLocations(prev => [...prev, locationId]);
    } else {
      setSelectedLocations(prev => prev?.filter(id => id !== locationId));
    }
  };

  const handleDepartmentChange = (departmentId, checked) => {
    if (checked) {
      setSelectedDepartments(prev => [...prev, departmentId]);
    } else {
      setSelectedDepartments(prev => prev?.filter(id => id !== departmentId));
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-card rounded-lg shadow-elevation-2 w-full max-w-2xl max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-border">
          <h2 className="text-xl font-semibold text-foreground">
            {translate('modals.announcement.title', 'Create Announcement')}
          </h2>
          <Button variant="ghost" onClick={onClose} className="p-2">
            <Icon name="X" size={20} />
          </Button>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="p-6 space-y-6">
          {/* Title */}
          <Input
            label={translate('modals.announcement.fields.title', 'Announcement Title')}
            type="text"
            value={title}
            onChange={(e) => setTitle(e?.target?.value)}
            placeholder={translate('modals.announcement.fields.titlePlaceholder', 'Enter announcement title')}
            required
          />

          {/* Message */}
          <div>
            <label className="block text-sm font-medium text-foreground mb-2">
              {translate('modals.announcement.fields.message', 'Message *')}
            </label>
            <textarea
              value={message}
              onChange={(e) => setMessage(e?.target?.value)}
              placeholder={translate('modals.announcement.fields.messagePlaceholder', 'Enter your announcement message...')}
              rows={4}
              required
              className="w-full px-3 py-2 bg-input border border-border rounded-lg text-sm text-foreground placeholder-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring resize-none"
            />
          </div>

          {/* Priority */}
          <div>
            <label className="block text-sm font-medium text-foreground mb-2">
              {translate('modals.announcement.fields.priority', 'Priority Level')}
            </label>
            <div className="flex space-x-4">
              {[
                {
                  value: 'low',
                  label: translate('modals.announcement.priorityOptions.low', 'Low'),
                  color: 'text-muted-foreground',
                },
                {
                  value: 'normal',
                  label: translate('modals.announcement.priorityOptions.normal', 'Normal'),
                  color: 'text-foreground',
                },
                {
                  value: 'high',
                  label: translate('modals.announcement.priorityOptions.high', 'High'),
                  color: 'text-error',
                },
              ]?.map((option) => (
                <label key={option?.value} className="flex items-center space-x-2 cursor-pointer">
                  <input
                    type="radio"
                    name="priority"
                    value={option?.value}
                    checked={priority === option?.value}
                    onChange={(e) => setPriority(e?.target?.value)}
                    className="w-4 h-4 text-primary border-border focus:ring-ring"
                  />
                  <span className={`text-sm ${option?.color}`}>{option?.label}</span>
                </label>
              ))}
            </div>
          </div>

          {/* Target Locations */}
          <div>
            <label className="block text-sm font-medium text-foreground mb-2">
              {translate('modals.announcement.fields.locations', 'Target Locations')}
            </label>
            <div className="space-y-2 max-h-32 overflow-y-auto border border-border rounded-lg p-3">
              <Checkbox
                label={translate('modals.announcement.fields.allLocations', 'All Locations')}
                checked={selectedLocations?.length === locations?.length}
                onChange={(e) => {
                  if (e?.target?.checked) {
                    setSelectedLocations(locations?.map(loc => loc?.id));
                  } else {
                    setSelectedLocations([]);
                  }
                }}
              />
              <div className="border-t border-border pt-2 mt-2">
                {locations?.map((location) => (
                  <Checkbox
                    key={location?.id}
                    label={location?.name}
                    checked={selectedLocations?.includes(location?.id)}
                    onChange={(e) => handleLocationChange(location?.id, e?.target?.checked)}
                  />
                ))}
              </div>
            </div>
          </div>

          {/* Target Departments */}
          <div>
            <label className="block text-sm font-medium text-foreground mb-2">
              {translate('modals.announcement.fields.departments', 'Target Departments')}
            </label>
            <div className="space-y-2 max-h-32 overflow-y-auto border border-border rounded-lg p-3">
              <Checkbox
                label={translate('modals.announcement.fields.allDepartments', 'All Departments')}
                checked={selectedDepartments?.length === departments?.length}
                onChange={(e) => {
                  if (e?.target?.checked) {
                    setSelectedDepartments(departments?.map(dept => dept?.id));
                  } else {
                    setSelectedDepartments([]);
                  }
                }}
              />
              <div className="border-t border-border pt-2 mt-2">
                {departments?.map((department) => (
                  <Checkbox
                    key={department?.id}
                    label={department?.name}
                    checked={selectedDepartments?.includes(department?.id)}
                    onChange={(e) => handleDepartmentChange(department?.id, e?.target?.checked)}
                  />
                ))}
              </div>
            </div>
          </div>

          {/* Options */}
          <div className="space-y-3">
            <Checkbox
              label={translate('modals.announcement.requireReceipt.label', 'Require read receipt')}
              description={translate(
                'modals.announcement.requireReceipt.description',
                'Track who has read this announcement',
              )}
              checked={requireReadReceipt}
              onChange={(e) => setRequireReadReceipt(e?.target?.checked)}
            />

            <Input
              label={translate('modals.announcement.fields.schedule', 'Schedule for later (optional)')}
              type="datetime-local"
              value={scheduleFor}
              onChange={(e) => setScheduleFor(e?.target?.value)}
              description={translate(
                'modals.announcement.fields.scheduleDescription',
                'Leave empty to send immediately',
              )}
            />
          </div>

          {/* Actions */}
          <div className="flex items-center space-x-3 pt-4 border-t border-border">
            <div className="flex-1 text-sm text-error truncate">
              {error}
            </div>
            <Button variant="outline" onClick={onClose}>
              {translate('modals.announcement.actions.cancel', 'Cancel')}
            </Button>
            <Button type="submit" disabled={isSubmitting || !title?.trim() || !message?.trim()}>
              <Icon name="Send" size={16} className="mr-2" />
              {isSubmitting
                ? translate('modals.announcement.actions.sending', 'Sending…')
                : scheduleFor
                  ? translate('modals.announcement.actions.schedule', 'Schedule Announcement')
                  : translate('modals.announcement.actions.send', 'Send Announcement')}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default AnnouncementModal;
